import { History } from 'history';
import type { Security_SessionSummary } from 'invApi';
import {
  isExternalModulePath,
  getSecurePostAuthRedirectInfo
} from './navigationHelpers';
import { extractSecurityAttributesFromSession } from './securityAttributeHelpers';
import { AuthLogger } from './logger';
import { AUTHENTICATION_ROUTES, FLOW_TIMING } from '../constants';

/**
 * Router state handed to the access-redirect interstitial so it can tell the
 * user which module was denied and where they are being taken.
 */
export interface AccessRedirectState {
  /** The accessible module path the interstitial should navigate to. */
  targetPath: string;
  /** Display name of the module the user requested but cannot access. */
  deniedApplicationName?: string;
  /** Display name of the accessible module the user is being sent to. */
  targetApplicationName?: string;
}

/**
 * Navigate to a path using the appropriate method
 * - External modules: window.location.href (full page navigation)
 * - Internal routes: React Router history (SPA navigation)
 *
 * @param redirectPath - The path to navigate to
 * @param history - React Router history object
 */
export function navigateToPath(redirectPath: string, history: History): void {
  if (isExternalModulePath(redirectPath)) {
    // External module - use full page navigation to leave current app
    window.location.href = redirectPath;
  } else {
    // Internal route - use React Router for SPA navigation
    history.push(redirectPath);
  }
}

export interface SecureRedirectOptions {
  /** Session summary from authentication response */
  sessionSummary?: Security_SessionSummary;
  /** Original redirect URL from storage */
  redirectUrl: string | null;
  /** Function to clear redirect URL from storage */
  clearRedirectUrl: () => void;
  /** React Router history object */
  history: History;
  /** Context for logging (e.g., 'login', 'idp', 'session-validation') */
  logContext?: string;
  /** Whether to add delay for state synchronization (IDP needs this) */
  addDelay?: boolean;
}

export interface SecureRedirectResult {
  /**
   * True when the redirect stays inside the authentication SPA (an internal
   * `history.push`, e.g. the no-access page or the access-redirect interstitial).
   * False when it leaves the app via a full-page navigation to another module.
   *
   * Callers that render the destination underneath themselves (e.g.
   * `SessionGuard`) use this to know they must stop showing their loading state
   * and let the internal destination render.
   */
  navigatedWithinApp: boolean;
}

/**
 * Handle secure post-authentication redirect with logging
 * Consolidates the redirect logic used across authentication components
 */
export function handleSecureRedirect({
  sessionSummary,
  redirectUrl,
  clearRedirectUrl,
  history,
  logContext = 'authentication',
  addDelay = false
}: SecureRedirectOptions): SecureRedirectResult {
  const userSecurityAttributes =
    extractSecurityAttributesFromSession(sessionSummary);

  const result = getSecurePostAuthRedirectInfo(
    redirectUrl,
    userSecurityAttributes
  );

  // Clear redirect URL from storage
  if (redirectUrl) clearRedirectUrl();

  // The no-access page and the interstitial are auth-app routes reached via
  // SPA navigation; an `allowed` outcome only stays in-app for internal paths.
  const navigatedWithinApp =
    result.outcome !== 'allowed' || !isExternalModulePath(result.redirectPath);

  const performRedirect = () => {
    switch (result.outcome) {
      case 'no-access':
        // The user cannot use any module. Send them to the dedicated page that
        // explains this and lets them log out, rather than into a module that
        // would only deny them access.
        AuthLogger.warn(`User has no accessible modules from ${logContext}:`, {
          originalRequest: redirectUrl
        });
        // Replace (not push) so validating directly on /no-module-access does
        // not stack a redundant history entry pointing back at itself.
        history.replace(AUTHENTICATION_ROUTES.NO_MODULE_ACCESS);
        return;

      case 'fallback':
        // The requested module is off-limits but another is available. Route
        // through the interstitial so the user understands what happened before
        // the cross-module redirect completes.
        AuthLogger.warn(
          `User redirected to fallback module from ${logContext}:`,
          { originalRequest: redirectUrl, finalPath: result.redirectPath }
        );
        history.push({
          pathname: AUTHENTICATION_ROUTES.ACCESS_REDIRECT,
          state: {
            targetPath: result.redirectPath,
            deniedApplicationName: result.deniedApplication?.name,
            targetApplicationName: result.targetApplication?.name
          } as AccessRedirectState
        });
        return;

      case 'allowed':
      default:
        navigateToPath(result.redirectPath, history);
    }
  };

  if (addDelay) {
    setTimeout(performRedirect, FLOW_TIMING.IDP_REDIRECT_SYNC_DELAY_MS);
  } else {
    performRedirect();
  }

  return { navigatedWithinApp };
}
